// Intent: the Napper moment brought to money — when reality drifts from the
// plan (salary due the 10th didn't arrive, rent wasn't ticked off), the app
// ASKS instead of silently assuming, and visibly updates the plan after the
// answer. One row per overdue item, three filled buttons, no nagging: "Jeszcze
// nie" keeps asking, "Nie było" closes the month, an amount closes it too.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { colors, radius, spacing } from '@/theme/tokens';
import type { PendingItem } from '@/utils/forecast';
import { formatPln } from '@/utils/format';

interface AdaptivePlanCardProps {
  pending: PendingItem[];
  /** "Wpłynęła" with the confirmed amount (grosze). */
  onIncomeConfirm: (id: string, amountGr: number) => void;
  /** "Nie było w tym miesiącu" — for either kind. */
  onSkip: (item: PendingItem) => void;
  /** "Zapłacone" on a fixed cost. */
  onCostPaid: (id: string) => void;
  /** "Jeszcze nie" — collapse for now, ask again next time. */
  onLater: (item: PendingItem) => void;
}

/** Small filled action button (house rule: buttons always have a background). */
function MiniButton({ label, bg, color, onPress }: { label: string; bg: string; color: string; onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => ({
        backgroundColor: bg,
        opacity: pressed ? 0.85 : 1,
        borderRadius: radius.full,
        paddingVertical: 6,
        paddingHorizontal: spacing.md,
        minHeight: 32,
        justifyContent: 'center',
      })}
    >
      <AppText variant="labelMd" color={color}>
        {label}
      </AppText>
    </Pressable>
  );
}

export function AdaptivePlanCard({ pending, onIncomeConfirm, onSkip, onCostPaid, onLater }: AdaptivePlanCardProps) {
  const { t } = useTranslation();
  // Which income row is showing the amount input; prefilled with the typical
  // amount so a matching salary is two taps: "Wpłynęła" -> "Zapisz".
  const [amountFor, setAmountFor] = useState<string | null>(null);
  const [amountRaw, setAmountRaw] = useState('');

  if (pending.length === 0) return null;

  const parseGr = (raw: string): number => {
    const cleaned = raw.replace(/\s/g, '').replace(',', '.');
    const val = Number.parseFloat(cleaned);
    return Number.isFinite(val) && val > 0 ? Math.round(val * 100) : 0;
  };

  return (
    <Card style={{ gap: spacing.sm }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
        <Ionicons name="swap-vertical-outline" size={20} color={colors.navy} />
        <AppText variant="headlineSm" style={{ flex: 1 }}>
          {t('adaptive.title')}
        </AppText>
      </View>

      {pending.map((item) => {
        const isIncome = item.kind === 'INCOME';
        const asking = amountFor === item.id;
        return (
          <View
            key={`${item.kind}-${item.id}`}
            style={{ gap: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.divider }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons
                name={isIncome ? 'cash-outline' : 'receipt-outline'}
                size={18}
                color={isIncome ? colors.primary : colors.warning}
              />
              <AppText variant="bodyLg" style={{ flex: 1 }}>
                {isIncome
                  ? t('adaptive.incomeRow', { name: item.label, day: item.day })
                  : t('adaptive.costRow', {
                      name: item.label,
                      amount: item.amountGr != null ? formatPln(item.amountGr) : '',
                      day: item.day,
                    })}
              </AppText>
            </View>

            {asking ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                <TextInput
                  value={amountRaw}
                  onChangeText={setAmountRaw}
                  keyboardType="decimal-pad"
                  autoFocus
                  accessibilityLabel={t('adaptive.amountLabel')}
                  style={{
                    flex: 1,
                    borderWidth: 1,
                    borderColor: colors.outlineVariant,
                    borderRadius: radius.button,
                    paddingHorizontal: spacing.md,
                    paddingVertical: 8,
                    fontSize: 16,
                    color: colors.textPrimary,
                    backgroundColor: colors.background,
                  }}
                />
                <AppText variant="labelLg">zł</AppText>
                <MiniButton
                  label={t('common.save')}
                  bg={colors.primary}
                  color="#FFFFFF"
                  onPress={() => {
                    const gr = parseGr(amountRaw);
                    if (gr > 0) {
                      setAmountFor(null);
                      setAmountRaw('');
                      onIncomeConfirm(item.id, gr);
                    }
                  }}
                />
              </View>
            ) : (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
                {isIncome ? (
                  <MiniButton
                    label={t('adaptive.arrived')}
                    bg={colors.primary}
                    color="#FFFFFF"
                    onPress={() => {
                      setAmountFor(item.id);
                      setAmountRaw(item.amountGr != null ? String(Math.round(item.amountGr / 100)) : '');
                    }}
                  />
                ) : (
                  <MiniButton label={t('adaptive.paid')} bg={colors.primary} color="#FFFFFF" onPress={() => onCostPaid(item.id)} />
                )}
                <MiniButton label={t('adaptive.notYet')} bg={colors.infoBg} color={colors.navy} onPress={() => onLater(item)} />
                <MiniButton label={t('adaptive.didNotHappen')} bg={colors.warningBg} color={colors.warning} onPress={() => onSkip(item)} />
              </View>
            )}
          </View>
        );
      })}
    </Card>
  );
}
